import { showErrorToast } from "../../../../../components/common/ShowToast";
import { FileComment } from "./processCSVFile";
import { ErrorComment } from "./processExcelComment";

export interface ErrorRow {
  id: number;
  data: string[];
}

interface JSONData {
  authorName: string;
  authorEmail: string;
  comment: string;
}

export const processJSONFile = async (
  file: File,
  stream_id: string,
  fieldsMapped: {
    last_name: string;
    first_name: string;
    email: string;
    phone_number: string;
    comment: string;
  },
): Promise<{
  message: string;
  errorOccured: boolean;
  comments: FileComment[];
  errorComments: ErrorComment[];
}> => {
  try {
    const rawData = await readFileAsText(file);
    const parsedData: JSONData[] = JSON.parse(rawData);
    const singleObjKeys = Object.keys(parsedData[0]);
    const headingFromJsonFile = singleObjKeys.map((key) => `${key}`);
    const correctCommentLineHeadings = headingFromJsonFile.length;
    const fieldsMappedKeys = Object.keys(
      fieldsMapped,
    ) as (keyof typeof fieldsMapped)[];
    const fieldsIndexMap: {
      field: string;
      mappedString: string;
      index: number;
    }[] = [];

    for (let i = 0; i < fieldsMappedKeys.length; i++) {
      const currentKey = fieldsMappedKeys[i];
      const currentValue = fieldsMapped[currentKey];
      const headingIndexInFile = headingFromJsonFile.indexOf(currentValue);
      fieldsIndexMap.unshift({
        field: currentKey,
        mappedString: currentValue,
        index: headingIndexInFile,
      });
    }
    const errorRows = parsedData.filter((record: JSONData) => {
      const keys = Object.keys(record);
      return keys.length !== correctCommentLineHeadings;
    });

    const errorRowsMapped: string[][] = errorRows.map((row) => {
      const values: string[] = Object.values(row);
      return values;
    });

    const errorComments: ErrorComment[] = errorRowsMapped.map(
      (errorComment, index) => {
        const extractedIndex = extractIndexs(fieldsIndexMap);
        const isSameColumn =
          extractedIndex.firstNameFileIndex ===
          extractedIndex.lastNameFileIndex;

        const rawName = errorComment[extractedIndex.firstNameFileIndex];
        const firstName = isSameColumn
          ? (rawName?.split(" ")[0] ?? "")
          : errorComment[extractedIndex.firstNameFileIndex];
        const lastName = isSameColumn
          ? (rawName?.split(" ").slice(1).join(" ") ?? "")
          : errorComment[extractedIndex.lastNameFileIndex];

        if (errorComment.length > correctCommentLineHeadings) {
          const unknownFieldsArray = errorComment.slice(
            correctCommentLineHeadings,
          );
          return {
            id: index,
            first_name: firstName,
            last_name: lastName,
            comment: errorComment[extractedIndex.commentFileIndex],
            email: errorComment[extractedIndex.emailFileIndex],
            phone_number: errorComment[extractedIndex.phoneNumberFileIndex],
            unknownFieldsArray: [...unknownFieldsArray],
          };
        }

        return {
          id: index,
          first_name: firstName,
          last_name: lastName,
          comment: errorComment[extractedIndex.commentFileIndex],
          email: errorComment[extractedIndex.emailFileIndex],
          phone_number: errorComment[extractedIndex.phoneNumberFileIndex],
          unknownFieldsArray: undefined,
        };
      },
    );

    const commentsRow = parsedData.filter((record: JSONData) => {
      const keys = Object.keys(record);
      return keys.length === correctCommentLineHeadings;
    });

    // changing the array of objs(e.g [{...}, {...}]) to array of array strings [[...], [...]]
    const commentsRowsMapped: string[][] = commentsRow.map((row) => {
      const values: string[] = Object.values(row);
      return values;
    });

    const comments: FileComment[] = commentsRowsMapped.map(
      (record: string[], index: number) => {
        const firstNameFileIndex = fieldsIndexMap.filter(
          (obj) => obj.field === "first_name",
        )[0].index;
        const lastNameFileIndex = fieldsIndexMap.filter(
          (obj) => obj.field === "last_name",
        )[0].index;
        const commentFileIndex = fieldsIndexMap.filter(
          (obj) => obj.field === "comment",
        )[0].index;
        const emailFileIndex = fieldsIndexMap.filter(
          (obj) => obj.field === "email",
        )[0].index;
        const phoneNumberFileIndex = fieldsIndexMap.filter(
          (obj) => obj.field === "phone_number",
        )[0].index;

        const isSameColumn = firstNameFileIndex === lastNameFileIndex;
        const rawName = record[firstNameFileIndex];

        return {
          id: index,
          first_name: isSameColumn
            ? (rawName?.split(" ")[0] ?? "")
            : record[firstNameFileIndex],
          last_name: isSameColumn
            ? (rawName?.split(" ").slice(1).join(" ") ?? "")
            : record[lastNameFileIndex],
          comment: record[commentFileIndex],
          email: record[emailFileIndex],
          phone_number: record[phoneNumberFileIndex],
        };
      },
    );

    return {
      message: "File processed successfully",
      errorOccured: false,
      comments,
      errorComments,
    };
  } catch (err) {
    return {
      message:
        typeof err === "string"
          ? err
          : "Failed to process the file. Please make sure it is not corrupted.",
      errorOccured: true,
      comments: [],
      errorComments: [],
    };
  }
};

const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const result = e.target?.result;
      if (result) {
        resolve(result.toString());
      } else {
        reject("Failed to read file");
      }
    };

    reader.onerror = () => reject("Error while reading file");

    reader.readAsText(file);
  });
};

export const extractJsonHeadings = async (
  file: File,
): Promise<string[] | string> => {
  try {
    const jsonData = await readFileAsText(file);
    const parsedData = JSON.parse(jsonData);
    const singleObjKeys = Object.keys(parsedData[0]);
    const headings = singleObjKeys.map((key) => `${key}`);
    return headings;
  } catch (error) {
    console.error(error);
    showErrorToast(
      "Failed to extract the headings.Please try selecting the file again",
    );
    return "An error occured";
  }
};

const extractIndexs = (
  fieldsIndexMap: {
    field: string;
    mappedString: string;
    index: number;
  }[],
): {
  firstNameFileIndex: number;
  lastNameFileIndex: number;
  commentFileIndex: number;
  emailFileIndex: number;
  phoneNumberFileIndex: number;
} => {
  const firstNameFileIndex = fieldsIndexMap.filter(
    (obj) => obj.field === "first_name",
  )[0].index;
  const lastNameFileIndex = fieldsIndexMap.filter(
    (obj) => obj.field === "last_name",
  )[0].index;
  const commentFileIndex = fieldsIndexMap.filter(
    (obj) => obj.field === "comment",
  )[0].index;
  const emailFileIndex = fieldsIndexMap.filter(
    (obj) => obj.field === "email",
  )[0].index;
  const phoneNumberFileIndex = fieldsIndexMap.filter(
    (obj) => obj.field === "phone_number",
  )[0].index;

  return {
    firstNameFileIndex,
    lastNameFileIndex,
    commentFileIndex,
    emailFileIndex,
    phoneNumberFileIndex,
  };
};
